/**
 * La oferta de retención del modal de baja (tanda «Baja con descuento», UD-17, contrato §2.3 a §2.5). Lógica
 * pura: sin React ni pedidos. El modal dibuja lo que esto devuelve.
 *
 * **Ningún número se inventa** (contrato §0): montos, meses y fechas salen del quote de Tesla. Una oferta a la
 * que le falta una pieza no se muestra a medias: es `null`, y el modal sigue a la confirmación de siempre.
 */
import {localeOf, parseDate} from './paid/subscriptionPageModel';

// Si a los 8 s el Backend no contestó si hay oferta, la baja sigue sin ella (contrato §3.1).
export const OFFER_TIMEOUT_MS = 8000;

function isNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}

function isIsoDate(value) {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && !!parseDate(value);
}

function hasDates(raw) {
    return isIsoDate(raw.starts_on) && isIsoDate(raw.ends_on) && isIsoDate(raw.back_to_full_on);
}

function normalized(raw, {feeBefore, feeAfter}) {
    if (!isNumber(feeBefore) || !isNumber(feeAfter) || !(feeAfter < feeBefore) || feeAfter < 0) return null;
    if (!isNumber(raw.percent) || !Number.isInteger(raw.months) || raw.months < 1) return null;
    if (!raw.currency || !hasDates(raw)) return null;
    return {
        percent: raw.percent,
        months: raw.months,
        feeBefore,
        feeAfter,
        currency: String(raw.currency).toUpperCase(),
        plusVat: raw.plus_vat === true,
        startsOn: raw.starts_on,
        endsOn: raw.ends_on,
        backToFullOn: raw.back_to_full_on,
        isAutomaticDebit: raw.is_automatic_debit === true,
    };
}

/** La `offer` de `GET retention-offer/` (§2.3) o la del 409 `RETENTION_QUOTE_CHANGED` (§2.4), o `null`. */
export function offerFromQuote(raw) {
    if (!raw || typeof raw !== 'object') return null;
    return normalized(raw, {feeBefore: raw.fee_before, feeAfter: raw.fee_after});
}

/** El `retention_offer` que devuelve `accept/` (con la forma de §2.5), o `null`. */
export function offerFromAccepted(raw) {
    if (!raw || typeof raw !== 'object') return null;
    return normalized(raw, {feeBefore: raw.full_amount, feeAfter: raw.amount});
}

/** Lo que se ahorra en toda la oferta: (precio de hoy − precio con descuento) × meses. */
export function savingsOf(offer) {
    return Math.round((offer.feeBefore - offer.feeAfter) * offer.months * 100) / 100;
}

function joinList(items, lang) {
    if (items.length < 2) return items.join('');
    if (typeof Intl !== 'undefined' && Intl.ListFormat) {
        return new Intl.ListFormat(localeOf(lang), {style: 'long', type: 'conjunction'}).format(items);
    }
    return items.join(', ');
}

/**
 * Los meses de la oferta con su nombre en el idioma de la pantalla: «noviembre y diciembre», o «noviembre,
 * diciembre y enero» con más de dos (copy.md, convenciones). Salen de `starts_on` y `ends_on`, no se cuentan.
 */
export function offerMonthsText(offer, lang) {
    const start = parseDate(offer.startsOn);
    const end = parseDate(offer.endsOn);
    if (!start || !end || end < start) return '';
    const formatter = new Intl.DateTimeFormat(localeOf(lang), {month: 'long'});
    const names = [];
    let month = new Date(start.getFullYear(), start.getMonth(), 1);
    // Tope de 12: un quote roto no puede colgar la pantalla armando una lista sin fin.
    while (month <= end && names.length < 12) {
        names.push(formatter.format(month));
        month = new Date(month.getFullYear(), month.getMonth() + 1, 1);
    }
    return joinList(names, lang);
}

/**
 * Parte un texto traducido alrededor de un valor interpolado, para darle estilo propio al valor sin tocar la
 * clave (el copy es exacto) y sin suponer en qué lugar de la frase va en cada idioma.
 */
export function splitAround(text, marker) {
    const index = text.indexOf(marker);
    if (index < 0) return {before: text, after: ''};
    return {before: text.slice(0, index), after: text.slice(index + marker.length)};
}
